import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { Chart, Segment } from '../../models/statistics';
import { OverlayDirective } from '../overlay/overlay.directive';
import { FormatNumberPipe } from '../pipes/format-number.pipe';

@Component({
  selector: 'pozi-donut-chart',
  imports: [OverlayDirective, CommonModule, FormatNumberPipe],
  templateUrl: './donut-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DonutChartComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);

  chart = input.required<Chart>();

  /**
   * The single guard for `Chart.data` being absent (the back end sends
   * `chart: {id}` with no `data` key when there is nothing to plot) — every
   * read goes through this so no site can re-introduce the unguarded access.
   */
  readonly chartData = computed(() => this.chart().data ?? []);

  segments: Segment[] = [];
  viewBoxSize = 42;
  viewBox = `0 0 ${this.viewBoxSize} ${this.viewBoxSize}`;
  center = this.viewBoxSize / 2;
  circumference = 100;
  strokeWidth = 4;
  borderStrokeWidth = 2;
  startOffset = 25;
  radius = this.circumference / (2 * Math.PI);
  ringStrokeColor = '#E8EEF4';
  borderStrokeColor = '#FEFEFE';
  cdkOverlayPositionPairs: ConnectedPosition[] = [
    {
      offsetX: 6,
      offsetY: 0,
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      panelClass: 'right',
    },
    {
      offsetX: -6,
      offsetY: 0,
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      panelClass: 'left',
    },
    {
      offsetX: 0,
      offsetY: 6,
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      panelClass: 'bottom',
    },
    {
      offsetX: 0,
      offsetY: -6,
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      panelClass: 'top',
    },
  ];

  constructor() {
    effect(() => {
      this.calculateSegments();
      this.cdr.detectChanges();
    });
  }

  ngOnInit() {
    this.calculateSegments();
  }

  calculateSegments(): void {
    let cumulativeValue = 0;

    this.segments = this.chartData()
      .filter((item) => item?.value)
      .map((item, i) => {
        const value = item.value;
        const segment: Segment = {
          color: item.color,
          value,
          main: {
            strokeDashArray: this.getStrokeDashOffset(value),
            strokeDashOffset:
              i === 0
                ? this.startOffset
                : this.getStrokeDashArray(cumulativeValue),
          },
        };
        cumulativeValue += value;

        if (this.shouldAddOverlap(i, value)) {
          const halfValue = value / 2;
          segment.overlap = {
            strokeDashArray: this.getStrokeDashOffset(halfValue),
            strokeDashOffset: this.getStrokeDashArray(
              cumulativeValue - halfValue
            ),
          };

          segment.overlapBorder = {
            strokeDashArray: this.getStrokeDashOffset(0),
            strokeDashOffset: this.getStrokeDashArray(cumulativeValue),
          };
        }

        return segment;
      })
      .reverse();
  }

  private getStrokeDashOffset(value: number): string {
    return `${value} ${100 - value}`;
  }

  private getStrokeDashArray(cumulativeValue: number): number {
    return this.circumference - cumulativeValue + this.startOffset;
  }

  private shouldAddOverlap(i: number, value: number): boolean {
    const items = this.chartData().filter((i) => i.value);
    const totalValue = Math.floor(
      items.reduce((sum, item) => sum + item.value, 0)
    );

    if (totalValue < 95 || totalValue > 100) {
      return false;
    }

    return items.length > 1 || (items.length === 1 && value < 100);
  }

  rotateByMaxValue(segments: Segment[]): Segment[] {
    if (!segments || segments.length < 2) return segments;

    const max = segments.reduce((prev, curr) =>
      curr.value > prev.value ? curr : prev
    );
    const maxIndex = segments.indexOf(max);
    return [
      ...segments.slice(maxIndex + 1),
      ...segments.slice(0, maxIndex),
      max,
    ];
  }
}
